import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Activity, ChevronRight, ChevronDown, Clock, ArrowRight, RefreshCw,
  Settings, Trash2, Plus, X, Code, MessageSquare, Cpu, Database,
  Radio, Search, Zap, AlertTriangle, GitBranch, Brain, FileText,
  Download, Clipboard, Check, Layers, Paintbrush, LayoutGrid, Link2, Eye,
} from 'lucide-react';
import { useTracesStore } from '@/stores/agents/traces-store';
import { SpanDetail, SpanKind, ThreadSummary, TraceLevelConfig } from '@/services/api/agentApi';

// ─── Span kind configuration ─────────────────────────────────────────────────

const spanKindConfig: Record<SpanKind, { label: string; color: string; bgColor: string; Icon: React.FC<any> }> = {
  user_message:        { label: 'User',         color: 'text-blue-700',    bgColor: 'bg-blue-500/10 border-blue-500/30',    Icon: MessageSquare },
  final_response:      { label: 'Response',     color: 'text-green-700',   bgColor: 'bg-green-500/10 border-green-500/30',  Icon: MessageSquare },
  agent_routing:       { label: 'Routing',      color: 'text-purple-700',  bgColor: 'bg-purple-500/10 border-purple-500/30', Icon: GitBranch },
  agent_execution:     { label: 'Agent',        color: 'text-indigo-700',  bgColor: 'bg-indigo-500/10 border-indigo-500/30', Icon: Cpu },
  llm_call:            { label: 'LLM',          color: 'text-violet-700',  bgColor: 'bg-violet-500/10 border-violet-500/30', Icon: Brain },
  knowledge_retrieval: { label: 'Knowledge',    color: 'text-cyan-700',    bgColor: 'bg-cyan-500/10 border-cyan-500/30',    Icon: Search },
  memory_access:       { label: 'Memory Read',  color: 'text-sky-700',     bgColor: 'bg-sky-500/10 border-sky-500/30',      Icon: Database },
  memory_write:        { label: 'Memory Write', color: 'text-teal-700',    bgColor: 'bg-teal-500/10 border-teal-500/30',    Icon: Database },
  mqtt_publish:        { label: 'MQTT Pub',     color: 'text-amber-700',   bgColor: 'bg-amber-500/10 border-amber-500/30',  Icon: Radio },
  mqtt_wait:           { label: 'MQTT Wait',    color: 'text-orange-700',  bgColor: 'bg-orange-500/10 border-orange-500/30', Icon: Clock },
  data_query:          { label: 'Data Query',   color: 'text-emerald-700', bgColor: 'bg-emerald-500/10 border-emerald-500/30', Icon: Database },
  api_call:            { label: 'API Call',     color: 'text-lime-700',    bgColor: 'bg-lime-500/10 border-lime-500/30',    Icon: Zap },
  evaluation:          { label: 'Evaluation',   color: 'text-pink-700',    bgColor: 'bg-pink-500/10 border-pink-500/30',    Icon: FileText },
  error:               { label: 'Error',        color: 'text-red-700',     bgColor: 'bg-red-500/10 border-red-500/30',      Icon: AlertTriangle },
  // Unified tracing span kinds
  ui_data_request:     { label: 'Data Request', color: 'text-blue-600',    bgColor: 'bg-blue-400/10 border-blue-400/30',    Icon: Download },
  workflow_execution:  { label: 'Workflow',     color: 'text-yellow-700',  bgColor: 'bg-yellow-500/10 border-yellow-500/30', Icon: Layers },
  workflow_node:       { label: 'Node',         color: 'text-yellow-600',  bgColor: 'bg-yellow-400/10 border-yellow-400/30', Icon: Paintbrush },
  ui_data_render:      { label: 'Render',       color: 'text-green-600',   bgColor: 'bg-green-400/10 border-green-400/30',  Icon: Eye },
  ui_action:           { label: 'UI Action',    color: 'text-rose-700',    bgColor: 'bg-rose-500/10 border-rose-500/30',    Icon: Link2 },
  device_data_received:{ label: 'Device Data',  color: 'text-fuchsia-700', bgColor: 'bg-fuchsia-500/10 border-fuchsia-500/30', Icon: LayoutGrid },
};

// ─── Utilities ────────────────────────────────────────────────────────────────

const timeAgo = (date: string) => {
  const diff = Date.now() - new Date(date).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const formatDuration = (ms: number) => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const formatTokens = (n: number) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const formatCost = (usd: number) => {
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
};

const initiatorBadge = (initiator: string) => {
  const map: Record<string, string> = {
    user: 'bg-blue-500/10 text-blue-700',
    schedule: 'bg-amber-500/10 text-amber-700',
    trigger: 'bg-purple-500/10 text-purple-700',
    agent: 'bg-indigo-500/10 text-indigo-700',
  };
  return map[initiator] || 'bg-gray-500/10 text-gray-600';
};

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    active: 'bg-blue-500/10 text-blue-700',
    completed: 'bg-green-500/10 text-green-700',
    error: 'bg-red-500/10 text-red-700',
  };
  return map[status] || 'bg-gray-500/10 text-gray-600';
};

const traceLevelLabel = (level: number) => {
  const labels: Record<number, string> = { 1: 'Minimal', 2: 'Standard', 3: 'Verbose' };
  return labels[level] || `Level ${level}`;
};

// ─── Visual Editor Pipeline Constants ────────────────────────────────────────

const PIPELINE_STEPS = [
  { key: 'Data Domain Agent', label: 'Data Discovery', color: '#6366f1', Icon: Database },
  { key: 'Layout Agent', label: 'Layout', color: '#8b5cf6', Icon: LayoutGrid },
  { key: 'Component Agent', label: 'Components', color: '#3b82f6', Icon: Layers },
  { key: 'Data Domain Agent:binding', label: 'Data Binding', color: '#06b6d4', Icon: Link2 },
  { key: 'Styles Agent', label: 'Styles', color: '#ec4899', Icon: Paintbrush },
] as const;

interface PipelineStepData {
  label: string;
  color: string;
  Icon: React.FC<any>;
  status: 'ok' | 'error' | 'skipped';
  durationMs: number;
  actionCount: number;
  spans: SpanDetail[];
}

/**
 * Parse a flat span list into pipeline steps for visual-editor threads.
 * Groups spans by agent name, maps to pipeline steps, and extracts canvas actions.
 */
function buildPipelineSteps(spans: SpanDetail[]): PipelineStepData[] {
  // Group spans by agent
  const byAgent = new Map<string, SpanDetail[]>();
  for (const span of spans) {
    if (!span.agent) continue;
    const existing = byAgent.get(span.agent) || [];
    existing.push(span);
    byAgent.set(span.agent, existing);
  }

  return PIPELINE_STEPS.map((step) => {
    // For "Data Domain Agent:binding", look for data binding spans
    let matchedSpans: SpanDetail[] = [];
    if (step.key === 'Data Domain Agent:binding') {
      const dataSpans = byAgent.get('Data Domain Agent') || [];
      matchedSpans = dataSpans.filter(
        (s) =>
          s.summary?.toLowerCase().includes('bind') ||
          s.summary?.toLowerCase().includes('data binding') ||
          s.kind === 'data_query'
      );
    } else if (step.key === 'Data Domain Agent') {
      // For discovery step, exclude binding spans so they don't double-count
      const dataSpans = byAgent.get('Data Domain Agent') || [];
      matchedSpans = dataSpans.filter(
        (s) =>
          !s.summary?.toLowerCase().includes('bind') &&
          !s.summary?.toLowerCase().includes('data binding') &&
          s.kind !== 'data_query'
      );
      // If no non-binding spans found, fall back to discovery-specific matches
      if (matchedSpans.length === 0) {
        matchedSpans = dataSpans.filter(
          (s) =>
            s.summary?.toLowerCase().includes('discover') ||
            s.summary?.toLowerCase().includes('schema')
        );
      }
    } else {
      matchedSpans = byAgent.get(step.key) || [];
    }

    const hasError = matchedSpans.some((s) => s.status === 'error');
    const totalDuration = matchedSpans.reduce((sum, s) => sum + (s.duration_ms || 0), 0);

    // Count canvas actions from detail payloads
    let actionCount = 0;
    for (const s of matchedSpans) {
      if (s.detail?.actions) {
        actionCount += Array.isArray(s.detail.actions) ? s.detail.actions.length : 1;
      } else if (s.detail?.actionCount) {
        actionCount += Number(s.detail.actionCount);
      }
    }

    return {
      label: step.label,
      color: step.color,
      Icon: step.Icon,
      status: matchedSpans.length === 0 ? 'skipped' : hasError ? 'error' : 'ok',
      durationMs: totalDuration,
      actionCount,
      spans: matchedSpans,
    };
  });
}

// ─── Pipeline Waterfall View ─────────────────────────────────────────────────

const PipelineWaterfall: React.FC<{
  spans: SpanDetail[];
  onSelectStep: (spans: SpanDetail[]) => void;
}> = ({ spans, onSelectStep }) => {
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const steps = useMemo(() => buildPipelineSteps(spans), [spans]);

  const maxDuration = Math.max(...steps.map((s) => s.durationMs), 1);

  return (
    <div className="border border-border rounded-lg p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Eye className="h-4 w-4 text-primary" />
        <h4 className="text-sm font-medium">Pipeline Waterfall</h4>
      </div>
      <div className="space-y-2">
        {steps.map((step, idx) => {
          const barWidth = step.status === 'skipped' ? 0 : Math.max((step.durationMs / maxDuration) * 100, 8);
          const statusColor =
            step.status === 'ok'
              ? '#22c55e'
              : step.status === 'error'
                ? '#ef4444'
                : '#9ca3af';
          const isExpanded = expandedStep === idx;

          return (
            <div key={step.label}>
              <button
                onClick={() => {
                  if (step.status !== 'skipped') {
                    setExpandedStep(isExpanded ? null : idx);
                    onSelectStep(step.spans);
                  }
                }}
                className={`w-full text-left group ${step.status === 'skipped' ? 'opacity-50 cursor-default' : 'cursor-pointer'}`}
              >
                <div className="flex items-center gap-3">
                  {/* Step label */}
                  <div className="flex items-center gap-1.5 w-32 shrink-0">
                    <step.Icon className="h-3.5 w-3.5" style={{ color: step.color }} />
                    <span className="text-xs font-medium truncate">{step.label}</span>
                  </div>

                  {/* Waterfall bar */}
                  <div className="flex-1 h-6 bg-muted/30 rounded relative overflow-hidden">
                    {step.status !== 'skipped' && (
                      <div
                        className="h-full rounded transition-all duration-300"
                        style={{
                          width: `${barWidth}%`,
                          backgroundColor: statusColor,
                          opacity: 0.7,
                        }}
                      />
                    )}
                    {step.status === 'skipped' && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-[10px] text-muted-foreground italic">skipped</span>
                      </div>
                    )}
                  </div>

                  {/* Metrics */}
                  <div className="flex items-center gap-2 w-36 shrink-0 justify-end">
                    {step.status !== 'skipped' && (
                      <>
                        <span className="text-[10px] text-muted-foreground">
                          {step.actionCount} action{step.actionCount !== 1 ? 's' : ''}
                        </span>
                        <span className="text-[10px] font-mono" style={{ color: statusColor }}>
                          {formatDuration(step.durationMs)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </button>

              {/* Expanded: Canvas Actions */}
              {isExpanded && step.spans.length > 0 && (
                <div className="ml-[140px] mt-1 mb-2 border-l-2 pl-3 space-y-1" style={{ borderColor: step.color }}>
                  {step.spans.map((span) => {
                    const actions = span.detail?.actions;
                    return (
                      <div key={span.span_id} className="text-xs">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span className={`w-1.5 h-1.5 rounded-full ${span.status === 'error' ? 'bg-red-500' : 'bg-green-500'}`} />
                          <span className="truncate flex-1">{span.summary}</span>
                          {span.duration_ms !== undefined && (
                            <span className="text-[10px] font-mono shrink-0">{formatDuration(span.duration_ms)}</span>
                          )}
                        </div>
                        {Array.isArray(actions) && actions.length > 0 && (
                          <div className="ml-4 mt-0.5 space-y-0.5">
                            {actions.map((action: any, actionIdx: number) => (
                              <div key={actionIdx} className="flex items-center gap-1.5 text-[10px] text-muted-foreground/80">
                                <Code className="h-2.5 w-2.5 shrink-0" />
                                <span className="font-mono">{action.type}</span>
                                {action.payload?.componentId && (
                                  <span className="truncate">- {action.payload.componentId.slice(0, 12)}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Trace Level Config Section ──────────────────────────────────────────────

const TraceLevelConfigSection: React.FC = () => {
  const { traceLevelConfigs, fetchConfig, setTraceLevel, deleteTraceLevel } = useTracesStore();
  const [open, setOpen] = useState(false);
  const [addScope, setAddScope] = useState<'organization' | 'user'>('organization');
  const [addScopeId, setAddScopeId] = useState('');
  const [addLevel, setAddLevel] = useState(2);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (open) fetchConfig();
  }, [open]);

  const systemConfig = traceLevelConfigs.find(c => c.scope === 'system');
  const overrides = traceLevelConfigs.filter(c => c.scope !== 'system');

  const handleSetSystemLevel = async (level: number) => {
    await setTraceLevel('system', 'default', level);
  };

  const handleAddOverride = async () => {
    if (!addScopeId.trim()) return;
    await setTraceLevel(addScope, addScopeId.trim(), addLevel);
    setAddScopeId('');
    setIsAdding(false);
  };

  return (
    <div className="border-b border-border">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium hover:bg-accent/50 transition-colors"
      >
        <Settings className="h-4 w-4 text-muted-foreground" />
        <span>Trace Level Configuration</span>
        {open ? <ChevronDown className="h-4 w-4 ml-auto" /> : <ChevronRight className="h-4 w-4 ml-auto" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          {/* System default */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground w-28">System Default:</span>
            <div className="flex gap-1">
              {[1, 2, 3].map(level => (
                <button
                  key={level}
                  onClick={() => handleSetSystemLevel(level)}
                  className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                    (systemConfig?.trace_level ?? 2) === level
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border hover:bg-accent'
                  }`}
                >
                  {traceLevelLabel(level)}
                </button>
              ))}
            </div>
          </div>

          {/* Overrides table */}
          {overrides.length > 0 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left px-3 py-1.5 font-medium">Scope</th>
                    <th className="text-left px-3 py-1.5 font-medium">ID</th>
                    <th className="text-left px-3 py-1.5 font-medium">Level</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {overrides.map((cfg) => (
                    <tr key={`${cfg.scope}-${cfg.scope_id}`} className="border-t border-border">
                      <td className="px-3 py-1.5 capitalize">{cfg.scope}</td>
                      <td className="px-3 py-1.5 font-mono text-[10px]">{cfg.scope_id}</td>
                      <td className="px-3 py-1.5">{traceLevelLabel(cfg.trace_level)}</td>
                      <td className="px-2 py-1.5">
                        <button
                          onClick={() => deleteTraceLevel(cfg.scope, cfg.scope_id)}
                          className="p-0.5 hover:bg-red-500/10 rounded transition-colors text-red-500"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add override */}
          {isAdding ? (
            <div className="flex items-center gap-2">
              <select
                value={addScope}
                onChange={(e) => setAddScope(e.target.value as any)}
                className="text-xs border border-border rounded px-2 py-1 bg-background"
              >
                <option value="organization">Organization</option>
                <option value="user">User</option>
              </select>
              <input
                value={addScopeId}
                onChange={(e) => setAddScopeId(e.target.value)}
                placeholder={addScope === 'organization' ? 'Org ID...' : 'User ID...'}
                className="text-xs border border-border rounded px-2 py-1 bg-background flex-1"
              />
              <select
                value={addLevel}
                onChange={(e) => setAddLevel(Number(e.target.value))}
                className="text-xs border border-border rounded px-2 py-1 bg-background"
              >
                <option value={1}>Minimal (1)</option>
                <option value={2}>Standard (2)</option>
                <option value={3}>Verbose (3)</option>
              </select>
              <button
                onClick={handleAddOverride}
                className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
              >
                Add
              </button>
              <button
                onClick={() => setIsAdding(false)}
                className="p-1 hover:bg-accent rounded transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsAdding(true)}
              className="flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <Plus className="h-3 w-3" /> Add Override
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Thread List Item ─────────────────────────────────────────────────────────

const ThreadListItem: React.FC<{
  thread: ThreadSummary;
  isSelected: boolean;
  onClick: () => void;
}> = ({ thread, isSelected, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 border-b border-border hover:bg-accent/50 transition-colors ${
        isSelected ? 'bg-accent' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium line-clamp-1 flex-1">
          {thread.title || thread.agent_route || `Thread ${thread.thread_id.slice(0, 8)}`}
        </p>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
      </div>
      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${initiatorBadge(thread.initiator)}`}>
          {thread.initiator}
        </span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${statusBadge(thread.status)}`}>
          {thread.status}
        </span>
        {thread.initiator_detail === 'visual-editor' && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-pink-500/10 text-pink-700">
            visual-editor
          </span>
        )}
        {thread.agent_route && (
          <span className="text-[10px] text-muted-foreground">{thread.agent_route}</span>
        )}
      </div>
      <div className="flex items-center gap-2 mt-1.5 text-[10px] text-muted-foreground">
        <span>{thread.turn_count} turns</span>
        <span className="text-border">|</span>
        <span>{thread.total_llm_calls} LLM</span>
        <span className="text-border">|</span>
        <span>{formatTokens(thread.total_tokens)} tok</span>
        {thread.total_duration_ms > 0 && (
          <>
            <span className="text-border">|</span>
            <span className="flex items-center gap-0.5">
              <Clock className="h-2.5 w-2.5" />
              {formatDuration(thread.total_duration_ms)}
            </span>
          </>
        )}
        <span className="ml-auto">{timeAgo(thread.created_at)}</span>
      </div>
    </button>
  );
};

// ─── Span Tree Node ───────────────────────────────────────────────────────────

const SpanTreeNode: React.FC<{
  span: SpanDetail;
  depth: number;
  selectedSpanId: string | null;
  onSelect: (span: SpanDetail) => void;
}> = ({ span, depth, selectedSpanId, onSelect }) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = span.children && span.children.length > 0;
  const config = spanKindConfig[span.kind] || { label: span.kind, color: 'text-gray-700', bgColor: 'bg-gray-500/10 border-gray-500/30', Icon: Code };
  const IconComp = config.Icon;
  const isSelected = selectedSpanId === span.span_id;
  const isError = span.status === 'error';

  return (
    <div>
      <div
        className={`flex items-center gap-1.5 py-1.5 px-2 rounded-md cursor-pointer transition-colors hover:bg-accent/50 ${
          isSelected ? 'bg-accent ring-1 ring-primary/30' : ''
        }`}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
        onClick={() => onSelect(span)}
      >
        {/* Expand/collapse toggle */}
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="p-0.5 hover:bg-accent rounded shrink-0"
          >
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}

        {/* Kind icon + badge */}
        <span className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${config.bgColor} ${config.color} shrink-0`}>
          <IconComp className="h-2.5 w-2.5" />
          {config.label}
        </span>

        {/* Agent */}
        <span className="text-[10px] text-muted-foreground shrink-0">{span.agent}</span>

        {/* Summary */}
        <span className="text-xs truncate flex-1">{span.summary}</span>

        {/* LLM inline info */}
        {span.kind === 'llm_call' && span.llm && (
          <span className="text-[10px] text-violet-600 shrink-0">
            {span.llm.model} ({formatTokens(span.llm.tokens_input + span.llm.tokens_output)} tok)
          </span>
        )}

        {/* Duration */}
        {span.duration_ms !== undefined && (
          <span className={`text-[10px] shrink-0 ${isError ? 'text-red-600' : 'text-muted-foreground'}`}>
            {formatDuration(span.duration_ms)}
          </span>
        )}

        {/* Error indicator */}
        {isError && <AlertTriangle className="h-3 w-3 text-red-500 shrink-0" />}
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div>
          {span.children!.map((child) => (
            <SpanTreeNode
              key={child.span_id}
              span={child}
              depth={depth + 1}
              selectedSpanId={selectedSpanId}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Span Detail Panel ────────────────────────────────────────────────────────

const ExpandableCode: React.FC<{ label: string; content: string }> = ({ label, content }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium hover:bg-accent/50 transition-colors"
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <Code className="h-3 w-3 text-muted-foreground" />
        {label}
      </button>
      {expanded && (
        <pre className="px-3 pb-3 text-[11px] bg-muted/30 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-words">
          {content}
        </pre>
      )}
    </div>
  );
};

const SpanDetailPanel: React.FC<{ span: SpanDetail; onClose: () => void }> = ({ span, onClose }) => {
  const config = spanKindConfig[span.kind] || { label: span.kind, color: 'text-gray-700', bgColor: 'bg-gray-500/10 border-gray-500/30', Icon: Code };
  const IconComp = config.Icon;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${config.bgColor} ${config.color}`}>
            <IconComp className="h-2.5 w-2.5" />
            {config.label}
          </span>
          <span className="text-sm font-medium">{span.agent}</span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-accent rounded transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Summary */}
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-1">Summary</h4>
          <p className="text-sm">{span.summary}</p>
        </div>

        {/* Metadata grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          <div>
            <span className="text-muted-foreground">Span ID</span>
            <p className="font-mono text-[10px]">{span.span_id}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Turn</span>
            <p>{span.turn_index}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Status</span>
            <p className={`font-medium ${span.status === 'error' ? 'text-red-600' : span.status === 'ok' ? 'text-green-600' : 'text-blue-600'}`}>
              {span.status}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Trace Level</span>
            <p>{traceLevelLabel(span.trace_level)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Started</span>
            <p>{new Date(span.started_at).toLocaleString()}</p>
          </div>
          {span.ended_at && (
            <div>
              <span className="text-muted-foreground">Ended</span>
              <p>{new Date(span.ended_at).toLocaleString()}</p>
            </div>
          )}
          {span.duration_ms !== undefined && (
            <div>
              <span className="text-muted-foreground">Duration</span>
              <p className="font-medium">{formatDuration(span.duration_ms)}</p>
            </div>
          )}
          {span.parent_span_id && (
            <div>
              <span className="text-muted-foreground">Parent Span</span>
              <p className="font-mono text-[10px]">{span.parent_span_id}</p>
            </div>
          )}
        </div>

        {/* Error */}
        {span.error_message && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            <h4 className="text-xs font-medium text-red-700 mb-1">Error</h4>
            <p className="text-sm text-red-800">{span.error_message}</p>
          </div>
        )}

        {/* LLM details */}
        {span.llm && (
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground">LLM Call Details</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs bg-violet-500/5 border border-violet-500/20 rounded-lg p-3">
              <div>
                <span className="text-muted-foreground">Model</span>
                <p className="font-medium">{span.llm.model}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Provider</span>
                <p>{span.llm.provider}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Tier</span>
                <p>{span.llm.tier}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Latency</span>
                <p>{formatDuration(span.llm.latency_ms)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Input Tokens</span>
                <p>{formatTokens(span.llm.tokens_input)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Output Tokens</span>
                <p>{formatTokens(span.llm.tokens_output)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Cost</span>
                <p className="font-medium">{formatCost(span.llm.cost_usd)}</p>
              </div>
              {span.llm.temperature !== undefined && (
                <div>
                  <span className="text-muted-foreground">Temperature</span>
                  <p>{span.llm.temperature}</p>
                </div>
              )}
            </div>

            {/* Prompt/Response expandable sections */}
            <div className="space-y-2">
              {span.llm.prompt_system && (
                <ExpandableCode label="System Prompt" content={span.llm.prompt_system} />
              )}
              {span.llm.prompt_user && (
                <ExpandableCode label="User Prompt" content={span.llm.prompt_user} />
              )}
              {span.llm.response && (
                <ExpandableCode label="Response" content={span.llm.response} />
              )}
            </div>
          </div>
        )}

        {/* Detail JSON */}
        {span.detail && Object.keys(span.detail).length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-1">Detail</h4>
            <pre className="text-[11px] bg-muted/30 border border-border rounded-lg p-3 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-words">
              {JSON.stringify(span.detail, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Filter Bar ───────────────────────────────────────────────────────────────

const FilterBar: React.FC<{
  visualEditorOnly: boolean;
  onToggleVisualEditor: () => void;
}> = ({ visualEditorOnly, onToggleVisualEditor }) => {
  const { filters, setFilter, fetchThreads } = useTracesStore();

  const handleChange = (key: 'initiator' | 'status' | 'fromDate' | 'toDate', value: string) => {
    setFilter(key, value);
  };

  // Trigger fetch when filters change
  useEffect(() => {
    fetchThreads();
  }, [filters.initiator, filters.status, filters.fromDate, filters.toDate, filters.agentRoute]);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Visual Editor filter chip */}
      <button
        onClick={onToggleVisualEditor}
        className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-colors ${
          visualEditorOnly
            ? 'bg-primary text-primary-foreground border-primary'
            : 'border-border hover:bg-accent text-muted-foreground'
        }`}
      >
        <Eye className="h-3 w-3" />
        Visual Editor
      </button>
      <select
        value={filters.initiator || ''}
        onChange={(e) => handleChange('initiator', e.target.value)}
        className="text-xs border border-border rounded px-2 py-1 bg-background"
      >
        <option value="">All Initiators</option>
        <option value="user">User</option>
        <option value="schedule">Schedule</option>
        <option value="trigger">Trigger</option>
        <option value="agent">Agent</option>
      </select>
      <select
        value={filters.status || ''}
        onChange={(e) => handleChange('status', e.target.value)}
        className="text-xs border border-border rounded px-2 py-1 bg-background"
      >
        <option value="">All Statuses</option>
        <option value="active">Active</option>
        <option value="completed">Completed</option>
        <option value="error">Error</option>
      </select>
      <input
        type="date"
        value={filters.fromDate || ''}
        onChange={(e) => handleChange('fromDate', e.target.value)}
        className="text-xs border border-border rounded px-2 py-1 bg-background"
        title="From date"
      />
      <input
        type="date"
        value={filters.toDate || ''}
        onChange={(e) => handleChange('toDate', e.target.value)}
        className="text-xs border border-border rounded px-2 py-1 bg-background"
        title="To date"
      />
    </div>
  );
};

// ─── Main TracesTab ───────────────────────────────────────────────────────────

export const TracesTab: React.FC = () => {
  const {
    threads, total, selectedThread, selectedSpans, spanTree, selectedSpan,
    isLoading, isLoadingSpans, error, page, pageSize, filters,
    fetchThreads, selectThread, selectSpan, clearSelection, setPage, setFilter,
  } = useTracesStore();
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  // Derive visual editor toggle from the store's agentRoute filter
  const visualEditorOnly = filters.agentRoute === 'visual-editor';
  const toggleVisualEditor = useCallback(() => {
    setFilter('agentRoute', visualEditorOnly ? '' : 'visual-editor');
  }, [visualEditorOnly, setFilter]);

  const isVisualEditorThread = selectedThread?.initiator_detail === 'visual-editor';

  const buildExportJson = () => {
    if (!selectedThread) return '';
    return JSON.stringify({ thread: selectedThread, spans: selectedSpans }, null, 2);
  };

  const handleCopyToClipboard = async () => {
    const json = buildExportJson();
    await navigator.clipboard.writeText(json);
    setCopiedToClipboard(true);
    setTimeout(() => setCopiedToClipboard(false), 2000);
  };

  const handleDownloadFile = () => {
    const json = buildExportJson();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trace-${selectedThread!.thread_id.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchThreads();
  }, [page]);

  const totalPages = Math.ceil(total / pageSize);

  if (isLoading && threads.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-muted-foreground">Loading activity traces...</div>
      </div>
    );
  }

  if (error && threads.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-destructive">{error}</p>
          <button onClick={() => fetchThreads()} className="text-sm text-primary hover:underline">Retry</button>
        </div>
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="h-full flex flex-col">
        <TraceLevelConfigSection />
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center space-y-4">
            <Activity className="h-16 w-16 mx-auto text-muted-foreground/40" />
            <h3 className="text-lg font-medium text-muted-foreground">No Activity Traces Yet</h3>
            <p className="text-sm text-muted-foreground/70 max-w-md">
              Activity traces will appear here as agents process requests. Each trace shows the full execution path including LLM calls, data queries, and agent routing decisions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <TraceLevelConfigSection />

      <div className="flex-1 flex min-h-0">
        {/* Left panel: Thread list */}
        <div className="w-80 border-r border-border flex flex-col shrink-0">
          <div className="p-3 border-b border-border space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{total} thread{total !== 1 ? 's' : ''}</span>
              <button
                onClick={() => fetchThreads()}
                className="p-1 hover:bg-accent rounded transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <FilterBar
              visualEditorOnly={visualEditorOnly}
              onToggleVisualEditor={toggleVisualEditor}
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {threads.map((t) => (
              <ThreadListItem
                key={t.thread_id}
                thread={t}
                isSelected={selectedThread?.thread_id === t.thread_id}
                onClick={() => selectThread(t)}
              />
            ))}
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-2 border-t border-border flex items-center justify-between">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="text-xs px-2 py-1 border border-border rounded hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              <span className="text-xs text-muted-foreground">
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className="text-xs px-2 py-1 border border-border rounded hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Center panel: Span tree */}
        <div className={`flex-1 overflow-y-auto ${selectedSpan ? 'border-r border-border' : ''}`}>
          {!selectedThread ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              <div className="text-center space-y-2">
                <ArrowRight className="h-8 w-8 mx-auto text-muted-foreground/40" />
                <p className="text-sm">Select a thread to view its trace</p>
              </div>
            </div>
          ) : isLoadingSpans ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              Loading spans...
            </div>
          ) : (
            <div className="p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium">
                    {selectedThread.title || `Thread ${selectedThread.thread_id.slice(0, 8)}`}
                  </h3>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${statusBadge(selectedThread.status)}`}>
                    {selectedThread.status}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground">
                    {formatTokens(selectedThread.total_tokens)} tokens | {formatCost(selectedThread.total_cost_usd)}
                  </span>
                  <button
                    onClick={handleCopyToClipboard}
                    className="p-1 hover:bg-accent rounded transition-colors"
                    title="Copy trace JSON to clipboard"
                  >
                    {copiedToClipboard
                      ? <Check className="h-3.5 w-3.5 text-green-600" />
                      : <Clipboard className="h-3.5 w-3.5 text-muted-foreground" />}
                  </button>
                  <button
                    onClick={handleDownloadFile}
                    className="p-1 hover:bg-accent rounded transition-colors"
                    title="Download trace as JSON file"
                  >
                    <Download className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                  <button
                    onClick={clearSelection}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
              {/* Pipeline waterfall for visual-editor threads */}
              {isVisualEditorThread && selectedSpans.length > 0 && (
                <PipelineWaterfall
                  spans={selectedSpans}
                  onSelectStep={(stepSpans) => {
                    if (stepSpans.length > 0) selectSpan(stepSpans[0]);
                  }}
                />
              )}
              <div className="space-y-0.5">
                {spanTree.map((span) => (
                  <SpanTreeNode
                    key={span.span_id}
                    span={span}
                    depth={0}
                    selectedSpanId={selectedSpan?.span_id ?? null}
                    onSelect={selectSpan}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right panel: Span detail */}
        {selectedSpan && (
          <div className="w-96 shrink-0">
            <SpanDetailPanel
              span={selectedSpan}
              onClose={() => selectSpan(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
};
